import { Info } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Tooltip, cn } from '@/components/ui';

interface InfoTooltipProps {
  /** Accessible name of the trigger, e.g. "Explain maximum residual". */
  label: string;
  /** The explanation. Keep it to a sentence or two. */
  children: ReactNode;
  className?: string;
}

/**
 * Small "i" button that explains a term. Opens on hover and keyboard focus
 * like a tooltip, and also on click or tap so touch users can read it; an
 * outside tap, Escape, or blur closes it.
 */
export const InfoTooltip = ({
  label,
  children,
  className,
}: InfoTooltipProps) => {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      content={children}
      open={open}
      onOpenChange={setOpen}
      className="max-w-72 text-left font-normal leading-relaxed"
    >
      <button
        type="button"
        aria-label={label}
        // Keep the tooltip's own pointer-down handling from closing it, so a
        // tap or click opens the explanation instead of toggling it away.
        onPointerDown={(event) => event.preventDefault()}
        onClick={(event) => {
          event.preventDefault();
          setOpen(true);
        }}
        className={cn(
          'inline-flex size-5 shrink-0 items-center justify-center rounded-full align-middle text-fg-muted transition-colors duration-150 hover:text-fg data-[state=delayed-open]:text-fg data-[state=instant-open]:text-fg',
          className
        )}
      >
        <Info aria-hidden="true" className="size-3.5" />
      </button>
    </Tooltip>
  );
};
